import { ref } from "vue";
import { readAuthSession } from "./authSession.mjs";

const STORAGE_KEY = "hr-current-company";

const companyId = ref(null);
const companyName = ref(null);
let initialized = false;

const parseCompanyId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const readStorageValue = (storage, key) => {
  try {
    return storage?.getItem?.(key) || null;
  } catch (_) {
    return null;
  }
};

const isAdminSession = (storage) => {
  const roleKeys = [
    readStorageValue(storage, "user-role"),
    readStorageValue(storage, "user-primary-role"),
  ];
  try {
    const storedRoles = JSON.parse(readStorageValue(storage, "user-roles") || "[]");
    if (Array.isArray(storedRoles)) {
      storedRoles.forEach((entry) => {
        roleKeys.push(typeof entry === "string" ? entry : entry?.key || entry?.role || entry?.name);
      });
    }
  } catch (_) {
    /* Invalid role cache must not grant an admin bypass. */
  }
  return roleKeys.some((role) => String(role || "").trim().toLowerCase() === "admin");
};

// H3 PR 2: the session company comes from the session marker (or a legacy pre-H3 token), not from the cookie JWT.
const resolveSessionCompanyId = (storage) =>
  readAuthSession(storage)?.companyId || parseCompanyId(readStorageValue(storage, "user-company-id"));

const canUseCompanyContext = (id, storage) => {
  if (!readAuthSession(storage) || isAdminSession(storage)) return true;
  const sessionCompanyId = resolveSessionCompanyId(storage);
  return Boolean(sessionCompanyId && sessionCompanyId === id);
};

const removeStoredCompanyContext = (storage) => {
  try {
    storage?.removeItem?.(STORAGE_KEY);
  } catch (_) {
    /* ignore storage issues */
  }
};

const persist = (storage = globalThis?.localStorage) => {
  try {
    if (!companyId.value) {
      removeStoredCompanyContext(storage);
      return;
    }
    storage?.setItem?.(
      STORAGE_KEY,
      JSON.stringify({ id: companyId.value, name: companyName.value || null })
    );
  } catch (_) {
    /* ignore storage issues */
  }
};

export const readStoredCompanyContext = (storage = globalThis?.localStorage) => {
  try {
    const raw = storage?.getItem?.(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parseCompanyId(parsed?.id);
    if (!id || !canUseCompanyContext(id, storage)) {
      removeStoredCompanyContext(storage);
      return null;
    }
    return { id, name: parsed.name || null };
  } catch (_) {
    removeStoredCompanyContext(storage);
    return null;
  }
};

const loadFromStorage = () => {
  if (initialized) return;
  initialized = true;
  const storedContext = readStoredCompanyContext();
  if (!storedContext) return;
  companyId.value = storedContext.id;
  companyName.value = storedContext.name;
};

export const getActiveCompanyId = () => {
  loadFromStorage();
  return companyId.value || null;
};

export const clearStoredCompanyContext = (storage = globalThis?.localStorage) => {
  companyId.value = null;
  companyName.value = null;
  initialized = false;
  removeStoredCompanyContext(storage);
};

export const useCompanyContext = () => {
  loadFromStorage();

  const setCompanyContext = (payload) => {
    const id = parseCompanyId(payload?.id);
    if (!id || !canUseCompanyContext(id, globalThis?.localStorage)) {
      clearStoredCompanyContext();
      return;
    }
    companyId.value = id;
    companyName.value = payload.name || null;
    persist();
  };

  const clearCompanyContext = () => {
    clearStoredCompanyContext();
  };

  return {
    companyId,
    companyName,
    setCompanyContext,
    clearCompanyContext,
  };
};
